import React from 'react';
import styled, { css } from 'styled-components';
import { rgba } from 'polished';
import { globalTheme, combineValues } from '@cld/component-theme';
import { asStyledComponent } from '@cld/component-enhancer';
import { theme } from './CardTheme';
import { BaseCard } from './BaseCard';

const cardFocusCss = css`
  :focus:not(:active) {
    box-shadow: 0 0 0 1px ${theme.border.color.active}, 0px 0px 0px 4px ${combineValues(theme.background.color.focus, (color) => rgba(color, 0.5))};
  }
`;

const cardSelectedCss = css`
  cursor: default;
  background-color: ${combineValues(theme.background.color.active, (color) => rgba(color, 0.15))};
  color: ${theme.font.color.active};
  box-shadow: 0 0 0 3px ${theme.border.color.active};
`;

const cardActiveCss = css`
  :active {
    ${cardSelectedCss}
  }
`;

const cardHoverCss = css`
  :hover {
    cursor: pointer;
    box-shadow: ${globalTheme.shadow.sm};
    background-color: ${theme.background.color.hover};
    color: ${theme.font.color.hover};
  }
`;

const cardDisabledCss = css`
  pointer-events: none;
  opacity: 0.7;
  background-color: ${theme.background.color.disabled};
  color: ${theme.font.color.disabled};
`;

const StyledBaseCard = styled(BaseCard).attrs(({ tabIndex }) => ({ tabIndex: tabIndex || 0 }))<{
  disabled?: boolean;
  selected?: boolean;
  size: 'sm' | 'md';
}>`
  ${({ size }) => css`
    width: ${theme.size[size].width};
    height: ${theme.size[size].height};
  `};
  ${(props) => (props.selected ? cardSelectedCss : css`${cardHoverCss} ${cardActiveCss} ${!props.disabled && cardFocusCss}`)};
  ${(props) => props.disabled && cardDisabledCss};
`;

const ContentWrapper = styled.div`
  border-radius: ${theme.border.radius};
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

export interface CardProps {
  selected?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md';
  children: React.ReactNode;
  className?: string;
  tabIndex?: number;
}

export const Card = asStyledComponent(({ children, size = 'md', ...props }: CardProps) => (
  <StyledBaseCard data-test="card" {...props} size={size}>
    <ContentWrapper data-test-specifier="content-wrapper">
      {children}
    </ContentWrapper>
  </StyledBaseCard>
));
